import React from 'react';
import { components } from '../../config/designTokens';

/**
 * Button Component
 * 
 * A reusable button component that follows the Nice Ice design system.
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant (primary, secondary, outline, ghost)
 * @param {string} props.size - Button size (sm, md, lg)
 * @param {boolean} props.fullWidth - Whether the button should take full width
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.leftIcon - Icon to display on the left
 * @param {React.ReactNode} props.rightIcon - Icon to display on the right
 * @param {string} props.href - If provided, renders as an anchor tag
 * @param {Object} props.rest - Additional props to pass to the button element
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  children,
  className = '',
  leftIcon,
  rightIcon,
  href,
  ...rest
}) => {
  // Get variant styles from design tokens
  const variantStyles = components.button[variant] || components.button.primary;
  
  // Size styles
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg',
  }[size];
  
  // Focus styles
  const focusStyles = components.focus.primary;
  
  // Combined classes
  const classes = `
    ${variantStyles}
    ${sizeStyles}
    ${focusStyles}
    ${fullWidth ? 'w-full' : ''}
    font-medium
    inline-flex items-center justify-center
    transition-all duration-200
    ${className}
  `.trim();
  
  // If href is provided, render as an anchor tag
  if (href) {
    return (
      <a
        href={href}
        className={classes}
        {...rest}
      >
        {leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="ml-2">{rightIcon}</span>}
      </a>
    );
  }
  
  // Otherwise, render as a button
  return (
    <button
      className={classes}
      {...rest}
    >
      {leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};

export default Button; 